import type { GraphemeLessonPart } from "@-/dev.improve.haniverse.lib.level";
import onetime from "@-/com.npmjs.onetime";
import { hasLength } from "@-/com.npmjs.ts-array-length";
import { eq } from "@-/com.npmjs.drizzle-orm";
import {
  getQuizManifestEntryBySlug,
  getSlideKeyBySlug,
} from "@-/dev.improve.haniverse.assets.slides";
import { actorSchema, type UserCard } from "@-/dev.improve.haniverse.database.schemas.actor.schema";
import {
  diffGraphemeEnrollment,
  type GraphemeEnrollmentState,
  readGraphemeEnrollmentState,
  undoGraphemeEnrollment,
} from "@-/dev.improve.haniverse.lib.cards.user-card.create";
import { ensureUserGraphemeMeaningCard } from "@-/dev.improve.haniverse.lib.cards.user-card.grapheme.meaning.create";
import type { ActorDatabase } from "@-/dev.improve.haniverse.database.actor";
import {
  persistStudyCardReviewBatch,
  undoStudyCardReview,
} from "@-/dev.improve.haniverse.lib.cards.review";
import { isLostCommitLeaderChangeError } from "@-/dev.improve.haniverse.lib.database.engine/leader-replay";
import { deferGraphemeUntilNextLocalDay } from "@-/dev.improve.haniverse.lib.cards.user-card.update.deferred-until";
import type { DrillType } from "@-/dev.improve.haniverse.lib.drill.entities";
import type { SentenceDrillState } from "@-/dev.improve.haniverse.lib.sentence.drill";
import { completeSlide, restoreSlideCompletion } from "@-/dev.improve.haniverse.lib.slide.complete";
import {
  answerQuizCard,
  diffQuizEnrollment,
  type QuizCardState,
  readQuizCardState,
  undoQuizEnrollment,
} from "@-/dev.improve.haniverse.lib.quiz.answer";

import type { LearnCardAction } from "#learn-card-action";
import { learnCardReviewIdsForOperation } from "#learn-card-operation";
import type { LearnCardActionReceipt, LearnCardPersistedEffect } from "#learn-card-receipt";
import { sentenceDrillCommitReviews } from "#sentence-drill-commit-reviews";

/** `tokenIndex → the in-study card for that word`; words without a card are absent. */
export type LearnCardSentenceCardSet = ReadonlyMap<
  number,
  { fsrsCardId: string; studyCardId: string }
>;

/**
 * Everything the persistence step needs to know about the card being acted on, captured at the
 * moment of commit. The snapshot is FROZEN by the caller: a query invalidation, a late speech
 * result, or a re-render after the commit must not change what gets written.
 *
 * Card resolution (which `fsrs_card` / `user_card` a drill maps to) is done by the caller: it
 * lives in the query layer, and the domain step only needs the resolved ids. `card: null` means
 * no in-study card matched, which is a legitimate state (the rating-less "Continue" card).
 */
export type LearnCardActionSnapshot =
  | {
      card: { fsrsCardId: string; studyCardId: string } | null;
      drillType: DrillType;
      grapheme: string;
      kind: "review";
    }
  | {
      cards: LearnCardSentenceCardSet;
      drillType: DrillType;
      kind: "sentence";
      state: SentenceDrillState;
      word: string;
    }
  | {
      grapheme: string;
      kind: "lesson";
      levelIndex: number;
      part?: GraphemeLessonPart;
      /** Single-reading 2-P1 slot (干 gan4, then 干 gan1). */
      pinyin?: string;
    }
  | { kind: "slide"; slideSlug: string }
  | { kind: "quiz"; quizSlug: string; source: "lesson" | "review" };

export type ExecuteLearnCardActionDeps = {
  /**
   * Enroll a new character through the free-tier gate — or, for a pronunciation-level lesson
   * (`part: "pronunciation"`), the readings of a character already in study. Resolves `false`
   * when the gate refused — the paywall has been shown and the learner stays on the card.
   * `pinyin` restricts a single-reading 2-P1 slot (干 gan4, then 干 gan1).
   */
  enroll: (grapheme: string, part?: GraphemeLessonPart, pinyin?: string) => Promise<boolean>;
};

/**
 * Persist exactly what an action means for its card, and nothing else.
 *
 * No navigation, no undo bookkeeping, no session flags: those are effects of the receipt this
 * returns, and they belong to the caller. Keeping this step pure over the database is what lets the
 * same function serve a swipe, a button, a shortcut key, a voice command and the fast-forward path,
 * and what makes it testable against a real database with no React tree.
 *
 * Exactly-once: review rows are named after `operationId`, so re-running this with the same
 * operation (a retry after a lost outcome) finds them already there and records nothing new — the
 * receipt reports `alreadyPersisted` on each review. Enrollment and deferral are idempotent by
 * construction. A slide's completion is the one write this does not wait for: it is queued and
 * written behind, because the card after a slide comes from the bundled catalogue.
 *
 * A rejection therefore means NOTHING landed — each write is one transaction, and a rolled-back
 * transaction had no effect — so the caller may offer any action again. The single exception is a
 * `commit` whose acknowledgement was lost (a web leader handover): that is settled here by
 * replaying the same operation, and only when the replay fails too does this reject with a
 * {@link LearnCardActionOutcomeUnknownError}, the one outcome that must be retried as itself.
 */
export async function executeLearnCardAction(
  args: ExecuteLearnCardActionArgs,
): Promise<LearnCardActionReceipt> {
  const prior = priorStateForOperation(args.operationId);
  let persisted: LearnCardPersistedEffect;
  try {
    persisted = await persistEffect(args, prior);
  } catch (error) {
    if (!isLostCommitLeaderChangeError(error)) {
      throw error;
    }
    try {
      persisted = await persistEffect(args, prior);
    } catch (replayError) {
      throw new LearnCardActionOutcomeUnknownError(args.operationId, { cause: replayError });
    }
  }
  return { action: args.action, operationId: args.operationId, persisted };
}

/**
 * The action's write may or may not have landed and could not be settled by a replay. Only the
 * SAME operation may be submitted again: its row ids make the retry exactly-once, whereas any
 * other action could stack a second review on top of one that did land.
 */
export class LearnCardActionOutcomeUnknownError extends Error {
  constructor(operationId: string, options: { cause: unknown }) {
    super(`Could not confirm whether learn card operation ${operationId} was saved`, options);
    this.name = "LearnCardActionOutcomeUnknownError";
  }
}

/**
 * Roll back what a saved action wrote, returning the learner's data to exactly what it was before
 * the action, so its card can be acted on afresh — EVERY kind of action, not only ratings. The next
 * card is resolved from this data: an accepted lesson that stayed enrolled, or a slide that stayed
 * complete, would be skipped when the learner steps back past it and swipes forward again.
 */
export async function undoLearnCardActionReceipt({
  actorDb,
  now,
  receipt,
}: {
  actorDb: ActorDatabase;
  now: Date;
  receipt: LearnCardActionReceipt;
}): Promise<void> {
  const { persisted } = receipt;
  switch (persisted.kind) {
    case "review":
    case "review-batch": {
      const reviews = persisted.kind === "review" ? [persisted.review] : persisted.reviews;
      for (const review of [...reviews].reverse()) {
        await undoStudyCardReview({
          actorDb: actorDb(import.meta.location),
          fsrsCardId: review.fsrsCardId,
          now,
          userCardReviewId: review.userCardReviewId,
        });
      }
      return;
    }
    case "enrollment": {
      await undoGraphemeEnrollment({ actorDb, changes: persisted.enrolledCards, now });
      return;
    }
    case "quiz-answer": {
      // The review first: it rolls a fresh schedule back to new, then the enrollment goes.
      await undoStudyCardReview({
        actorDb: actorDb(import.meta.location),
        fsrsCardId: persisted.review.fsrsCardId,
        now,
        userCardReviewId: persisted.review.userCardReviewId,
      });
      if (persisted.enrolled !== null) {
        await undoQuizEnrollment({ actorDb, change: persisted.enrolled, now });
      }
      return;
    }
    case "deferral":
    case "marked-known":
    case "skipped": {
      await actorDb(import.meta.location)
        .update(actorSchema.userCard)
        .set(persisted.restore.columns)
        .where(eq(actorSchema.userCard.id, persisted.restore.userCardId))
        .run();
      return;
    }
    case "slide-completion": {
      await restoreSlideCompletion({
        actorDb,
        completionId: receipt.operationId,
        key: persisted.slideKey,
        now,
      });
      return;
    }
    case "none": {
      return;
    }
  }
}

/**
 * What an operation's FIRST attempt saw before writing, which its undo restores. Every later
 * attempt of the same operation reuses it — the replay of a lost commit, the deck's retry after a
 * timed-out write, a history redo's Retry — because by then the first attempt's write may have
 * landed, and reading "before" again would capture the action's own result, leaving its undo
 * nothing to reverse.
 */
type LearnCardPriorState = {
  enrollment?: GraphemeEnrollmentState;
  quizCard?: QuizCardState;
  userCard?: UserCard;
};

/** Retries follow within moments of their first attempt; older operations are never retried. */
const PRIOR_STATE_OPERATION_LIMIT = 64;
const getPriorStateByOperation = onetime(() => new Map<string, LearnCardPriorState>());

function priorStateForOperation(operationId: string): LearnCardPriorState {
  const byOperation = getPriorStateByOperation();
  const existing = byOperation.get(operationId);
  if (existing !== undefined) {
    return existing;
  }
  const prior: LearnCardPriorState = {};
  byOperation.set(operationId, prior);
  const [oldest] = byOperation.keys();
  if (oldest !== undefined && byOperation.size > PRIOR_STATE_OPERATION_LIMIT) {
    byOperation.delete(oldest);
  }
  return prior;
}

type ExecuteLearnCardActionArgs = {
  action: LearnCardAction;
  actorDb: ActorDatabase;
  deps: ExecuteLearnCardActionDeps;
  now: Date;
  operationId: string;
  snapshot: LearnCardActionSnapshot;
};

async function persistEffect(
  { action, actorDb, deps, now, operationId, snapshot }: ExecuteLearnCardActionArgs,
  prior: LearnCardPriorState,
): Promise<LearnCardPersistedEffect> {
  switch (action.kind) {
    case "mark-known":
    case "skip-lesson": {
      if (snapshot.kind !== "lesson") {
        throw new Error(`Only a lesson card supports ${action.kind} (got ${snapshot.kind})`);
      }
      // These mark the character's meaning card; a pronunciation-level lesson offers neither.
      if (snapshot.part === "pronunciation") {
        throw new Error(`A pronunciation lesson does not support ${action.kind}`);
      }
      const card = await ensureUserGraphemeMeaningCard(actorDb, snapshot.grapheme);
      const before = (prior.userCard ??= card);
      // Match the existing reversible card actions, without enrolling or creating a review.
      // Keep the first timestamp stable across retries.
      const update =
        action.kind === "mark-known"
          ? { manuallyMasteredAt: card.manuallyMasteredAt ?? now.toISOString() }
          : { skippedAt: card.skippedAt ?? now.toISOString() };
      await actorDb(import.meta.location)
        .update(actorSchema.userCard)
        .set({ deletedAt: null, ...update })
        .where(eq(actorSchema.userCard.id, card.id));
      return {
        grapheme: snapshot.grapheme,
        kind: action.kind === "mark-known" ? "marked-known" : "skipped",
        restore: {
          columns:
            action.kind === "mark-known"
              ? { deletedAt: before.deletedAt, manuallyMasteredAt: before.manuallyMasteredAt }
              : { deletedAt: before.deletedAt, skippedAt: before.skippedAt },
          userCardId: before.id,
        },
      };
    }
    case "continue": {
      if (snapshot.kind === "slide") {
        const slideKey = getSlideKeyBySlug(snapshot.slideSlug);
        if (slideKey === undefined) {
          throw new Error(`No slide key registered for slug "${snapshot.slideSlug}".`);
        }
        // Slides ship in the app bundle, so the card after one never waits on this write: the slide
        // reads as complete at once and its row is written behind (see `completeSlide`). The
        // operation names the completion, which is how undo takes back exactly this one; a retry
        // under the same name keeps the first attempt's record of the earlier state.
        void completeSlide({ actorDb, completionId: operationId, key: slideKey, now });
        return { kind: "slide-completion", slideKey };
      }
      return { kind: "none", reason: "no-op" };
    }

    case "rate": {
      if (snapshot.kind === "review") {
        if (snapshot.card === null) {
          return { kind: "none", reason: "unresolved-card" };
        }
        const [result] = await persistStudyCardReviewBatch({
          actorDb: actorDb(import.meta.location),
          now,
          reviews: [
            {
              fsrsCardId: snapshot.card.fsrsCardId,
              rating: action.rating,
              studyCardId: snapshot.card.studyCardId,
              ...learnCardReviewIdsForOperation(operationId),
            },
          ],
        });
        if (result === undefined) {
          throw new Error(`Expected a persisted review for operation ${operationId}, got none`);
        }
        return {
          drillType: snapshot.drillType,
          familiarGrapheme: result.familiarGrapheme,
          grapheme: snapshot.grapheme,
          kind: "review",
          review: {
            alreadyPersisted: result.alreadyPersisted,
            fsrsCardId: snapshot.card.fsrsCardId,
            studyCardId: snapshot.card.studyCardId,
            userCardReviewId: result.userCardReviewId,
          },
        };
      }

      if (snapshot.kind === "sentence") {
        // Resolve the words to cards FIRST, then number the reviews by their position in the
        // resolved list: a word the learner never enrolled has no card and gets no review, and
        // the ids must be stable across a retry, which they are because the snapshot is frozen.
        const reviewInputs = sentenceDrillCommitReviews(snapshot.state, action.rating).flatMap(
          ({ rating, tokenIndex }) => {
            const card = snapshot.cards.get(tokenIndex);
            return card === undefined ? [] : [{ ...card, rating }];
          },
        );
        if (hasLength(reviewInputs, 0)) {
          return { familiarGraphemes: [], kind: "review-batch", reviews: [], word: snapshot.word };
        }
        const results = await persistStudyCardReviewBatch({
          actorDb: actorDb(import.meta.location),
          now,
          reviews: reviewInputs.map((input, index) => ({
            ...input,
            ...learnCardReviewIdsForOperation(operationId, index),
          })),
        });
        return {
          familiarGraphemes: results.flatMap((result) =>
            result.familiarGrapheme === null ? [] : [result.familiarGrapheme],
          ),
          kind: "review-batch",
          reviews: results.map((result, index) => {
            const input = reviewInputs[index];
            if (input === undefined) {
              throw new Error(`Persisted more sentence reviews than were requested`);
            }
            return {
              alreadyPersisted: result.alreadyPersisted,
              fsrsCardId: input.fsrsCardId,
              studyCardId: input.studyCardId,
              userCardReviewId: result.userCardReviewId,
            };
          }),
          word: snapshot.word,
        };
      }

      throw new Error(`A ${snapshot.kind} card cannot be rated`);
    }

    case "answer-quiz": {
      if (snapshot.kind !== "quiz") {
        throw new Error(`Only a quiz card can be answered (got ${snapshot.kind})`);
      }
      const quizKey = getQuizManifestEntryBySlug(snapshot.quizSlug)?.key;
      if (quizKey === undefined) {
        throw new Error(`No quiz registered for slug "${snapshot.quizSlug}".`);
      }
      prior.quizCard ??= await readQuizCardState(actorDb, quizKey);
      const review = await answerQuizCard({
        actorDb,
        correct: action.correct,
        key: quizKey,
        now,
        review: learnCardReviewIdsForOperation(operationId),
      });
      return {
        enrolled: diffQuizEnrollment(prior.quizCard, await readQuizCardState(actorDb, quizKey)),
        kind: "quiz-answer",
        quizKey,
        review: {
          alreadyPersisted: review.alreadyPersisted,
          fsrsCardId: review.fsrsCardId,
          studyCardId: review.studyCardId,
          userCardReviewId: review.userCardReviewId,
        },
      };
    }

    case "accept-lesson": {
      if (snapshot.kind !== "lesson") {
        throw new Error(`Only a lesson card can be accepted (got ${snapshot.kind})`);
      }
      prior.enrollment ??= await readGraphemeEnrollmentState(actorDb, snapshot.grapheme);
      const wasAdded = await deps.enroll(snapshot.grapheme, snapshot.part, snapshot.pinyin);
      return {
        enrolledCards: wasAdded
          ? diffGraphemeEnrollment(
              prior.enrollment,
              await readGraphemeEnrollmentState(actorDb, snapshot.grapheme),
            )
          : [],
        grapheme: snapshot.grapheme,
        kind: "enrollment",
        wasAdded,
      };
    }

    case "defer-lesson": {
      if (snapshot.kind !== "lesson") {
        throw new Error(`Only a lesson card can be deferred (got ${snapshot.kind})`);
      }
      // A deferral holds back the character's meaning lesson; the pronunciation lesson has none.
      if (snapshot.part === "pronunciation") {
        throw new Error("A pronunciation lesson cannot be deferred");
      }
      const before = (prior.userCard ??= await ensureUserGraphemeMeaningCard(
        actorDb,
        snapshot.grapheme,
      ));
      const { deferredUntil } = await deferGraphemeUntilNextLocalDay({
        actorDb,
        grapheme: snapshot.grapheme,
        now,
      });
      return {
        deferredUntil,
        grapheme: snapshot.grapheme,
        kind: "deferral",
        restore: {
          columns: { deferredUntil: before.deferredUntil, deletedAt: before.deletedAt },
          userCardId: before.id,
        },
      };
    }
  }
}
